import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { observer } from 'mobx-react';
import { useCallback, useEffect, useState } from 'react';
import { useFeatureFlag } from '@marvelapp/ballpark-application';
import { BtwDropdown, BtwLink, Icon, SquareButton, Transcription as TranscriptionPrimitive, } from '@marvelapp/ballpark-components';
import { CONTACT_SUPPORT } from '@marvelapp/ballpark-help';
import { AiTextIcon, DownloadIcon, TranscriptionIcon, WarningBubbleIcon, } from '@marvelapp/ballpark-icons';
import { DOTS_ICON } from '@marvelapp/ballpark-theme';
import { formatTime } from '@marvelapp/core';
import { AttachedMediaStatusEnum, AttachedMediaStatusReasonEnum, } from '../../__generated__/queryTypes';
const NAME_COLORS = ['green', 'purple', 'blue', 'yellow', 'red'];
function getTranscriptionStatusMessage(transcriptionMedia) {
    if (!transcriptionMedia)
        return null;
    switch (transcriptionMedia.status) {
        case AttachedMediaStatusEnum.FAILED:
            return (_jsx(TranscriptionPrimitive.StatusMessage, { icon: _jsx(WarningBubbleIcon, {}), heading: "Transcription failed", description: _jsxs(_Fragment, { children: ["Unfortunately we couldn't generate a transcription for this recording.", ' ', _jsx(BtwLink.External, { href: CONTACT_SUPPORT, children: "Contact support" }), "."] }) }));
        case AttachedMediaStatusEnum.PENDING:
            return (_jsx(TranscriptionPrimitive.StatusMessage, { icon: _jsx(TranscriptionIcon, {}), heading: "Generating transcription", description: _jsx(_Fragment, { children: "Transcription is currently being generated. It'll appear here soon." }) }));
        case AttachedMediaStatusEnum.WARNING: {
            if (transcriptionMedia.statusReason ===
                AttachedMediaStatusReasonEnum.NO_AUDIO ||
                transcriptionMedia.statusReason ===
                    AttachedMediaStatusReasonEnum.NO_SPOKEN_AUDIO) {
                return (_jsx(TranscriptionPrimitive.StatusMessage, { icon: _jsx(TranscriptionIcon, {}), heading: "Transcription unavailable", description: "It looks like there was no audio or not enough words detected in a supported language for us to generate a transcription." }));
            }
            return null;
        }
        case AttachedMediaStatusEnum.SUCCESS:
        case AttachedMediaStatusEnum.NOT_ALLOWED:
        case AttachedMediaStatusEnum.NOT_REQUIRED:
        case AttachedMediaStatusEnum.NOT_SUPPORTED:
            return null;
        default:
            throw new Error(`Unknown transcription status: ${transcriptionMedia.status}`);
    }
}
function getSummaryStatusMessage(summaryMedia) {
    if (!summaryMedia)
        return null;
    switch (summaryMedia.status) {
        case AttachedMediaStatusEnum.FAILED: {
            if (summaryMedia.statusReason ===
                AttachedMediaStatusReasonEnum.NON_ENGLISH_AUDIO) {
                return (_jsx(TranscriptionPrimitive.StatusMessage, { icon: _jsx(AiTextIcon, {}), heading: "Non-English summarization coming soon", description: "Transcript summaries are currently only available for responses taken in English. We are adding more languages soon." }));
            }
            return (_jsx(TranscriptionPrimitive.StatusMessage, { icon: _jsx(WarningBubbleIcon, {}), heading: "Failed to generate a summary", description: _jsxs(_Fragment, { children: ["Unfortunately we couldn't generate a summary for this recording.", _jsx(BtwLink.External, { href: CONTACT_SUPPORT, children: "Contact support" }), "."] }) }));
        }
        case AttachedMediaStatusEnum.PENDING:
            return (_jsx(TranscriptionPrimitive.StatusMessage, { icon: _jsx(AiTextIcon, {}), heading: "Generating summary", description: _jsx(_Fragment, { children: "Summary is currently being generated. It'll appear here soon." }) }));
        case AttachedMediaStatusEnum.WARNING: {
            if (summaryMedia.statusReason === AttachedMediaStatusReasonEnum.NO_AUDIO ||
                summaryMedia.statusReason ===
                    AttachedMediaStatusReasonEnum.NO_SPOKEN_AUDIO) {
                return (_jsx(TranscriptionPrimitive.StatusMessage, { icon: _jsx(AiTextIcon, {}), heading: "Summary unavailable", description: "It looks like there was no audio or not enough words detected in a supported language for us to generate a summary." }));
            }
            return null;
        }
        case AttachedMediaStatusEnum.SUCCESS:
        case AttachedMediaStatusEnum.NOT_ALLOWED:
        case AttachedMediaStatusEnum.NOT_REQUIRED:
        case AttachedMediaStatusEnum.NOT_SUPPORTED:
            return null;
        default:
            throw new Error(`Unknown summary status: ${summaryMedia.status}`);
    }
}
export const Transcription = observer(function Transcription({ transcriptionMedia, summaryMedia, time, className, onSeekTo, }) {
    var _a;
    const { transcription, summary, isLoading } = useLoadTranscription(transcriptionMedia);
    const isSummaryEnabled = useFeatureFlag('media-transcription-summary');
    const hasSummary = summary && isSummaryEnabled;
    if (!transcriptionMedia ||
        transcriptionMedia.status === AttachedMediaStatusEnum.NOT_ALLOWED ||
        transcriptionMedia.status === AttachedMediaStatusEnum.NOT_REQUIRED ||
        // NOT_SUPPORTED is for Janus recordings so we don't show anything
        transcriptionMedia.status === AttachedMediaStatusEnum.NOT_SUPPORTED ||
        isLoading) {
        return null;
    }
    // TODO error state for loadig the actual file?
    const transcriptionStatus = getTranscriptionStatusMessage(transcriptionMedia);
    const summaryStatus = summaryMedia
        ? getSummaryStatusMessage(summaryMedia)
        : null;
    // TODO expose an isLiveSession flag from the graphql query?
    const numberOfSpeakers = ((_a = summary === null || summary === void 0 ? void 0 : summary.content) === null || _a === void 0 ? void 0 : _a.speakerLabels)
        ? Object.keys(summary.content.speakerLabels).length
        : 0;
    const isMaybeLiveSession = numberOfSpeakers > 1;
    const activeIndex = getActiveTranscriptionIndex(transcription, time);
    const activeChapterIndex = hasSummary
        ? getActiveChapterIndex(summary, time)
        : -1;
    return (_jsxs(TranscriptionPrimitive.Root, { className: className, defaultTab: hasSummary ? 'Summary' : 'Transcription', actions: transcriptionMedia.url && (_jsx(TranscriptionActions, { downloadURL: transcriptionMedia.url })), children: [isSummaryEnabled && (_jsxs(TranscriptionPrimitive.SummaryTab, { children: [summaryStatus, summary && (_jsx(TranscriptionPrimitive.Summary, { activeChapterIndex: activeChapterIndex, summary: summary.content, onSeekTo: onSeekTo }))] })), _jsxs(TranscriptionPrimitive.TranscriptionTab, { children: [transcriptionStatus, transcription.map((segment, index) => {
                        var _a, _b, _c;
                        const speakerNumber = (_a = segment.speaker) !== null && _a !== void 0 ? _a : 0;
                        const speakerColor = NAME_COLORS[speakerNumber % NAME_COLORS.length];
                        const speakerKey = `Speaker ${speakerNumber}`;
                        const speakerLabel = isMaybeLiveSession
                            ? (_c = (_b = summary === null || summary === void 0 ? void 0 : summary.content) === null || _b === void 0 ? void 0 : _b.speakerLabels) === null || _c === void 0 ? void 0 : _c[speakerKey]
                            : undefined;
                        return (_jsx(TranscriptionPrimitive.Item, { index: index, isActive: index === activeIndex, text: segment.text, timestamp: formatTime(segment.start, false), onClick: () => onSeekTo === null || onSeekTo === void 0 ? void 0 : onSeekTo(segment.start), name: speakerLabel, type: speakerLabel ? 'name' : 'transcription-segment', nameVariant: speakerLabel ? speakerColor : undefined }, segment.id));
                    })] })] }));
});
const TranscriptionActions = observer(function TranscriptionActions({ downloadURL, }) {
    const handleDownload = useCallback(() => {
        if (!downloadURL)
            return;
        const link = document.createElement('a');
        link.href = downloadURL;
        link.download = 'transcription.vtt';
        link.click();
    }, [downloadURL]);
    return (_jsxs(BtwDropdown.Root, { children: [_jsx(BtwDropdown.Trigger, { "data-testid": "transcription-dropdown-button", asChild: true, children: _jsx(SquareButton, { as: "div", translateOnHover: false, size: "m", kind: "transparent", children: _jsx(Icon, { paths: DOTS_ICON }) }) }), _jsx(BtwDropdown.Content, { align: "end", children: _jsx(BtwDropdown.Item, { leadingIcon: _jsx(DownloadIcon, {}), "data-testid": "download-transcription-button", onClick: handleDownload, disabled: !downloadURL, children: "Download transcription" }) })] }));
});
function useLoadTranscription(transcriptionMedia) {
    const [transcription, setTranscription] = useState([]);
    const [summary, setSummary] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState();
    useEffect(() => {
        if (!transcriptionMedia.url)
            return;
        setIsLoading(true);
        setError(undefined);
        fetch(transcriptionMedia.url)
            .then((response) => response.json())
            .then((data) => {
            const { transcript, summary } = parseTranscription(data) || {};
            if (transcript)
                setTranscription(formatTranscription(transcript));
            if (summary)
                setSummary(summary);
        })
            .catch((error) => {
            console.error('Error fetching transcription', error);
            setError('Failed to load transcription. Please try again later.');
        })
            .finally(() => {
            setIsLoading(false);
        });
    }, [transcriptionMedia.url]);
    return { transcription, summary, isLoading, error };
}
function formatTranscription(transcription) {
    var _a, _b, _c, _d, _e, _f;
    const paragraphs = (_f = (_e = (_d = (_c = (_b = (_a = transcription.results) === null || _a === void 0 ? void 0 : _a.channels) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.alternatives) === null || _d === void 0 ? void 0 : _d[0]) === null || _e === void 0 ? void 0 : _e.paragraphs) === null || _f === void 0 ? void 0 : _f.paragraphs;
    if (!paragraphs)
        return [];
    return paragraphs.map((paragraph) => ({
        id: paragraph.start.toString(),
        isActive: false,
        start: paragraph.start,
        end: paragraph.end,
        text: paragraph.sentences.map((sentence) => sentence.text).join('\n'),
        speaker: paragraph.speaker,
    }));
}
function getActiveTranscriptionIndex(transcription, time) {
    return binarySearch(transcription, (segment) => segment.start <= time && segment.end >= time, (segment) => segment.end < time, false);
}
function getActiveChapterIndex(summary, time) {
    const { chapters } = summary.content;
    if (!chapters.length || time < chapters[0].timestamp)
        return -1;
    return binarySearch(chapters, (chapter) => chapter.timestamp <= time, (chapter) => chapter.timestamp < time, true);
}
/**
 * Binary search that finds an element based on a predicate and navigation function
 * @param array The array to search in
 * @param predicate Function that returns true for matching elements
 * @param navigate Function that determines search direction when no match is found
 * @param findLast If true, continues searching after finding a match
 */
function binarySearch(array, predicate, navigate, findLast = false) {
    if (!array.length)
        return -1;
    let left = 0;
    let right = array.length - 1;
    let result = -1;
    while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        const item = array[mid];
        if (predicate(item)) {
            result = mid;
            if (findLast) {
                left = mid + 1;
            }
            else {
                // Return immediately if we're not looking for last match
                return mid;
            }
        }
        else if (navigate(item)) {
            left = mid + 1;
        }
        else {
            right = mid - 1;
        }
    }
    return result;
}
function parseTranscription(data) {
    if (!data)
        return null;
    if (data && 'version' in data) {
        return data;
    }
    return { transcript: data };
}
